import axios, { AxiosRequestConfig } from 'axios';
import {
  captureException,
  createAxiosInterceptor,
  DISTRIBUTED_TRACE_HEADER,
} from '@tracewayapp/react';
// config
import { HOST_API } from 'src/config-global';

export const INTERNAL_SERVER_ERROR = "An unexpected error has occured. We're looking into it.";
export const UNKNOWN_ERROR =
  'An unexpected error has occured. Please check your internet connection.';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: HOST_API, withCredentials: true });

// The Traceway SDK only propagates traceway-trace-id on same-origin requests,
// and the API lives on another origin: stamp every request ourselves and
// capture failures with the same id so frontend and backend errors link up.
axiosInstance.interceptors.request.use(createAxiosInterceptor());

axiosInstance.interceptors.response.use(
  (res) => res,
  (error) => {
    captureException(error, {
      distributedTraceId: error?.config?.headers?.[DISTRIBUTED_TRACE_HEADER] || undefined,
    });
    return Promise.reject(error);
  }
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res.data;
};

// ----------------------------------------------------------------------

export type ParametrizedEndpoint = {
  endpoint: string;
  params: string[];
};
export type Endpoint = ParametrizedEndpoint | string;

export function formatEndpoint(endpoint: Endpoint, data: any) {
  if (typeof endpoint === 'string') {
    return endpoint;
  }
  endpoint = endpoint as ParametrizedEndpoint;

  let finalEndpoint = endpoint.endpoint;
  endpoint.params.forEach((param) => {
    finalEndpoint = finalEndpoint.split(`:${param}`).join(encodeURIComponent(data[param]));
  });
  return finalEndpoint;
}

export const endpoints = {
  auth: {
    current: '/ifta/current',
    login: '/ifta/login',
    register: '/ifta/register',
    googleStart: '/ifta/auth/start/google',
  },
  filings: {
    findAll: '/ifta/filings',
    create: '/ifta/filings',
    single: { endpoint: '/ifta/filings/:id', params: ['id'] },
    update: { endpoint: '/ifta/filings/:id', params: ['id'] },
    destroy: { endpoint: '/ifta/filings/:id', params: ['id'] },
    uploadMiles: { endpoint: '/ifta/filings/:id/miles/upload', params: ['id'] },
    autoImportMiles: { endpoint: '/ifta/filings/:id/miles/auto-import', params: ['id'] },
    uploadFuel: { endpoint: '/ifta/filings/:id/fuel/upload', params: ['id'] },
    removeMilesFile: { endpoint: '/ifta/filings/:id/miles/remove-file', params: ['id'] },
    removeFuelFile: { endpoint: '/ifta/filings/:id/fuel/remove-file', params: ['id'] },
    updateMileLines: { endpoint: '/ifta/filings/:id/miles/lines', params: ['id'] },
    updateFuelLines: { endpoint: '/ifta/filings/:id/fuel/lines', params: ['id'] },
    acknowledgeIssue: { endpoint: '/ifta/filings/:id/acknowledge-issue', params: ['id'] },
    calculate: { endpoint: '/ifta/filings/:id/calculate', params: ['id'] },
    checkout: { endpoint: '/ifta/filings/:id/checkout', params: ['id'] },
    verifyPayment: { endpoint: '/ifta/filings/:id/verify-payment', params: ['id'] },
    report: { endpoint: '/ifta/filings/:id/report', params: ['id'] },
    reportExcel: { endpoint: '/ifta/filings/:id/report.xlsx', params: ['id'] },
    markFiled: { endpoint: '/ifta/filings/:id/mark-filed', params: ['id'] },
  },
  rates: {
    byQuarter: { endpoint: '/ifta/rates/:quarter', params: ['quarter'] },
  },
};
